import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import SimpleHero from '../../components/SimpleHero/SimpleHero';
import FeatureList from '../../components/Minimal/FeatureList';
import directImg from '../../assets/images/DirectDeposit.png';
import './Direct.css';
import jsPDF from 'jspdf';

const setupSteps = [
  {
    number: '01',
    title: 'Sign in to your account',
    description: 'Open the GNBank mobile app or online banking and navigate to the account where you want your paycheck deposited.'
  },
  {
    number: '02',
    title: 'Get your account details',
    description: 'Tap "Account & routing numbers" to view your information. Copy both numbers or download the direct deposit authorization letter.'
  },
  {
    number: '03',
    title: 'Access your payroll portal',
    description: 'Log into your employer or benefits payroll portal, select "Add bank account" or "Direct deposit," and choose "Checking" as the account type.'
  },
  {
    number: '04',
    title: 'Enter your bank information',
    description: 'Paste the routing number (271070924) and your GNBank account number. Choose 100% of your pay or set up split deposits if preferred.'
  },
  {
    number: '05',
    title: 'Confirm and wait',
    description: 'Submit the form and watch for confirmation. The change typically takes 1-2 pay cycles. We\'ll notify you when your first direct deposit arrives.'
  }
];

const routingTips = [
  {
    icon: '🔢',
    title: 'Routing Number',
    value: '271070924',
    description: 'This is the same for all GNBank customers'
  },
  {
    icon: '💳',
    title: 'Account Number',
    value: 'Your unique number',
    description: 'Find it in the app under Account Details or on your account statement'
  },
  {
    icon: '📄',
    title: 'Paper Form',
    value: 'Download available',
    description: 'Get the direct deposit authorization letter from the app to give to HR'
  },
  {
    icon: '🏛️',
    title: 'Government Benefits',
    value: 'Same process',
    description: 'Use the same routing and account numbers for SSA, VA, or unemployment'
  }
];

const DirectDepositPage = () => {
  const [copied, setCopied] = useState(false);
  const [accountNumber, setAccountNumber] = useState('');
  const [accountEmployer, setAccountEmployer] = useState('');
  const [accountType, setAccountType] = useState('Checking');
  const [logoBase64, setLogoBase64] = useState(null)
  const handleCopyRouting = () => {
    navigator.clipboard.writeText('271070924');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };


 // Load logo and convert to base64
 useEffect(() => {
  const loadLogo = async () => {
    try {
      // Using logo192.png from public folder
      const response = await fetch('/logo192.png');
      const blob = await response.blob();
      const reader = new FileReader();
      
      reader.onloadend = () => {
        setLogoBase64(reader.result);
      };
      
      reader.readAsDataURL(blob);
    } catch (error) {
      console.error('Error loading logo:', error);
    }
  };
  
  loadLogo();
}, []);
  

  const generateDirectDepositPDF = () => {
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 20;
    let yPosition = margin;


 // Add Logo at the top
 if (logoBase64) {
  try {
    // Logo dimensions - adjust as needed
    const logoWidth = 40;
    const logoHeight = 40;
    const logoX = (pageWidth - logoWidth) / 2; // Center the logo
    const logoY = yPosition;
    
    doc.addImage(logoBase64, 'PNG', logoX, logoY, logoWidth, logoHeight);
    yPosition += logoHeight + 10; // Add space after logo
  } catch (error) {
    console.error('Error adding logo to PDF:', error);
  }
}






    // Header
    // doc.setFontSize(20);
    // doc.setFont('helvetica', 'bold');
    // doc.setTextColor(130, 17, 17); // GN Bank primary color
    // doc.text('GN Bank', pageWidth / 2, yPosition, { align: 'center' });
    // yPosition += 10;



    doc.setFontSize(16);
    doc.text('Direct Deposit Authorization Form', pageWidth / 2, yPosition, { align: 'center' });
    yPosition += 15;

    // Date
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(0, 0, 0);
    const currentDate = new Date().toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
    doc.text(`Date: ${currentDate}`, margin, yPosition);
    yPosition += 15;

    // Instructions
    doc.setFontSize(11);
    doc.setFont('helvetica', 'bold');
    doc.text('Please provide this information to your employer or benefits provider:', margin, yPosition);
    yPosition += 10;

    // Bank Information Section
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    


    doc.setFont('helvetica', 'bold');
    doc.text('Employer:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    const employer = accountEmployer || '[Your Employer Name]';
    doc.text(employer, margin + 60, yPosition);
    yPosition += 8;



    // Routing Number
    doc.setFont('helvetica', 'bold');
    doc.text('Routing Number (ABA):', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setFont('courier', 'bold');
    doc.text('271070924', margin + 60, yPosition);
    yPosition += 8;

    // Account Number
    doc.setFont('helvetica', 'bold');
    doc.text('Account Number:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.setFont('courier', 'bold');
    const accountNum = accountNumber || '[Your Account Number]';
    doc.text(accountNum, margin + 60, yPosition);
    yPosition += 8;

    // Account Type
    doc.setFont('helvetica', 'bold');
    doc.text('Account Type:', margin, yPosition);
    doc.setFont('helvetica', 'normal');
    doc.text(accountType, margin + 60, yPosition);
    yPosition += 15;

    // Authorization Section
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(11);
    doc.text('Authorization:', margin, yPosition);
    yPosition += 8;

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(10);
    const employerName = accountEmployer || 'the above named Company';
    const authText = `I (we) authorize ${employerName} to initiate credit entries to my GN Bank ${accountType} and/or Savings account(s) listed above and to credit the same to such account(s). I (we) acknowledge that the origination of the ACH transactions to my (our) account must comply with the provisions of U.S. Law.`;

 
   
    
    
    const splitText = doc.splitTextToSize(authText, pageWidth - (margin * 2));
    doc.text(splitText, margin, yPosition);
    yPosition += splitText.length * 5 + 10;

    // Signature Section
    if (yPosition > pageHeight - 60) {
      doc.addPage();
      yPosition = margin;
    }

    doc.setFont('helvetica', 'bold');
    doc.text('Employee/Beneficiary Signature:', margin, yPosition);
    yPosition += 8;
    
    // Signature line
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, pageWidth - margin, yPosition);
    yPosition += 5;
    
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    doc.text('(Please sign and date)', margin, yPosition);
    yPosition += 10;

    doc.setFont('helvetica', 'bold');
    doc.text('Date:', margin, yPosition);
    yPosition += 8;
    doc.setLineWidth(0.5);
    doc.line(margin, yPosition, margin + 60, yPosition);
    yPosition += 15;

    // Footer
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.text('For questions, please contact GN Bank customer service.', pageWidth / 2, pageHeight - 15, { align: 'center' });
    doc.text('Routing Number: 271070924 | www.gnbank.net', pageWidth / 2, pageHeight - 10, { align: 'center' });

    return doc;
  };

  const handleDownloadPDF = () => {
    const doc = generateDirectDepositPDF();
    const fileName = `GNBank_DirectDeposit_${new Date().toISOString().split('T')[0]}.pdf`;
    doc.save(fileName);
  };

  const handlePrintPDF = () => {
    const doc = generateDirectDepositPDF();
    doc.autoPrint();
    doc.output('dataurlnewwindow');
  };









  // Get the current URL for sharing
  const currentUrl = typeof window !== 'undefined' 
    ? window.location.origin + window.location.pathname
    : 'https://gnbank.net/DirectDeposit';
  
  // OG image URL - using the public og.png or can be updated to a specific direct deposit image
  const ogImageUrl = 'https://gnbank.net/og.png';

  return (
    <div className="direct-deposit-page">
      <Helmet>
        <title>Direct Deposit - Get Paid Up to 2 Days Early | GN Bank</title>
        <meta 
          name="description" 
          content="Set up direct deposit with GN Bank and get paid up to 2 days early. Find your routing number (061000104), account number, and step-by-step setup instructions." 
        />
        
        {/* Open Graph / Facebook / LinkedIn */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={currentUrl} />
        <meta property="og:title" content="Direct Deposit - Get Paid Up to 2 Days Early | GN Bank" />
        <meta 
          property="og:description" 
          content="Set up direct deposit with GN Bank and get paid up to 2 days early. Faster paydays, always secure, hands-free. Routing number: 271070924." 
        />
        <meta property="og:image" content={ogImageUrl} />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:site_name" content="GN Bank" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content={currentUrl} />
        <meta name="twitter:title" content="Direct Deposit - Get Paid Up to 2 Days Early | GN Bank" />
        <meta 
          name="twitter:description" 
          content="Set up direct deposit with GN Bank and get paid up to 2 days early. Routing number: 271070924." 
        />
        <meta name="twitter:image" content={ogImageUrl} />
        
        {/* Additional meta tags */}
        <meta name="keywords" content="direct deposit, routing number, GN Bank, early paycheck, direct deposit setup, account number" />
        <link rel="canonical" href={currentUrl} />
      </Helmet>
      
      <SimpleHero
        title="Get paid up to 2 days early"
        subtitle="Set up direct deposit for faster, safer paydays—no trips to the branch."
        primaryLabel="Set up direct deposit"
        primaryHref="/DirectDeposit"
        variant="sofi"
        imageSrc={directImg}
        imageAlt="Direct deposit"
        imageOn="right"
        tiltImage
        badges={['Faster paydays', 'Always secure', 'Hands-free']}
      />

      <FeatureList
        items={[
          { title: 'Faster paydays', desc: 'Access funds up to 2 days early.' },
          { title: 'Always secure', desc: 'Your money moves safely every time.' },
          { title: 'Hands-free', desc: 'No trips or waiting—arrives automatically.' },
        ]}
      />

      {/* Routing & Account Number Section */}
      <section className="direct-deposit-section direct-deposit-section--highlight">
        <div className="gn-container">
          <div className="direct-deposit-header">
            <h2 className="direct-deposit-title">Your Direct Deposit Information</h2>
            <p className="direct-deposit-subtitle">Use these numbers when setting up direct deposit with your employer or benefits provider</p>
          </div>
          
          <div className="bank-info-cards">
            <div className="bank-info-card bank-info-card--routing">
              <div className="bank-info-card__header">
                <span className="bank-info-card__icon">🏦</span>
                <h3 className="bank-info-card__title">Routing Number</h3>
              </div>
              <div className="bank-info-card__value-wrapper">
                <code className="bank-info-card__value">271070924</code>
                <button 
                  className="bank-info-card__copy-btn"
                  onClick={handleCopyRouting}
                  aria-label="Copy routing number"
                >
                  {copied ? '✓ Copied' : 'Copy'}
                </button>
              </div>
              <p className="bank-info-card__description">Same for all GNBank customers</p>
            </div>
            <div className="bank-info-card bank-info-card--account">
              <div className="bank-info-card__header">
                <span className="bank-info-card__icon">💳</span>
                <h3 className="bank-info-card__title">Account Information</h3>
              </div>
              
              <div className="direct-deposit-form">
                <div className="form-group">
                  <label className="form-label">Account Number</label>
                  <input
                    type="text"
                    className="bank-info-card__input"
                    placeholder="Enter your account number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Employer Name</label>
                  <input
                    type="text"
                    className="bank-info-card__input"
                    placeholder="Enter your employer name"
                    value={accountEmployer}
                    onChange={(e) => setAccountEmployer(e.target.value)}
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">Account Type</label>
                  <select
                    className="bank-info-card__select"
                    value={accountType}
                    onChange={(e) => setAccountType(e.target.value)}
                  >
                    <option value="Checking">Checking</option>
                    <option value="Savings">Savings</option>
                  </select>
                </div>
              </div>

              <p className="bank-info-card__description">Fill in your information to generate the direct deposit form</p>

              {/* PDF Actions */}
              <div className="pdf-actions">
                <button 
                  className="btn btn-gn-primary pdf-action-btn"
                  onClick={handleDownloadPDF}
                  disabled={!accountNumber || !accountEmployer}
                >
                  📥 Download PDF
                </button>
                <button 
                  className="btn btn-gn-secondary pdf-action-btn"
                  onClick={handlePrintPDF}
                  disabled={!accountNumber || !accountEmployer}
                >
                  🖨️ Print PDF
                </button>
              </div>
            </div>
          


          </div>
        </div>
      </section>

      {/* Setup Steps Section */}
      <section className="direct-deposit-section">
        <div className="gn-container">
          <div className="direct-deposit-header">
            <h2 className="direct-deposit-title">How to Set Up Direct Deposit</h2>
            <p className="direct-deposit-subtitle">Follow these five simple steps to get started</p>
          </div>

          <div className="setup-steps">
            {setupSteps.map((step, index) => (
              <div key={index} className="setup-step-card">
                <div className="setup-step-card__number">{step.number}</div>
                <div className="setup-step-card__content">
                  <h3 className="setup-step-card__title">{step.title}</h3>
                  <p className="setup-step-card__description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Information Section */}
      <section className="direct-deposit-section direct-deposit-section--muted">
        <div className="gn-container">
          <div className="direct-deposit-header">
            <h2 className="direct-deposit-title">Need More Information?</h2>
            <p className="direct-deposit-subtitle">Everything you need to know about direct deposit</p>
          </div>

          <div className="info-grid">
            {routingTips.map((tip, index) => (
              <div key={index} className="info-card">
                <div className="info-card__icon">{tip.icon}</div>
                <h3 className="info-card__title">{tip.title}</h3>
                <p className="info-card__value">{tip.value}</p>
                <p className="info-card__description">{tip.description}</p>
              </div>
            ))}
          </div>



 


          

          <div className="help-section">
            <p className="help-text">
              <strong>Still need help?</strong> Call the number on the back of your debit card or chat with us in the app. 
              We'll walk you through the entire process step by step.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="direct-deposit-section direct-deposit-section--cta">
        <div className="gn-container">
          <div className="cta-content">
            <h2 className="cta-title">Ready to Get Started?</h2>
            <p className="cta-subtitle">Download the GNBank app and set up direct deposit in minutes</p>
            <a className="btn btn-gn-primary btn-gn-primary--large" href="/download">
              Download the App
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default DirectDepositPage;